import styled, {css, keyframes} from 'styled-components';

import {colors} from '@app/styles/theme';
import {Buttons} from '@shared/ui/Pagination/styled';

export const Root = styled.div`
    position: relative;
`;

export const TableView = styled.div<{
    tableViewExtraSettings?: Record<string, string>;
    hoverableRow?: boolean;
}>`
    width: 100%;
    overflow-x: ${(p) => p.tableViewExtraSettings?.overflowX ?? 'auto'};
    -webkit-overflow-scrolling: touch;

    .table {
        text-align: left;
        font-variant-numeric: tabular-nums;
        border-collapse: collapse;
        font-size: 12px;
        //min-height: 650px;
    }

    .thead {
        .tr {
            background-color: ${colors.background};
        }

        .groupHeader {
            .th {
                min-height: 5px;
                padding-top: 10px;
                background-color: ${colors.background};
            }
        }
    }

    .td,
    .th {
        vertical-align: top;
        padding: ${(p) => p.tableViewExtraSettings?.tdThPadding ?? '5px 5px 5px 20px'};
        min-height: 50px;
    }

    .th {
        font-weight: 500;
        display: inline-flex !important;
        align-items: center;
        user-select: none;
    }

    .tr {
        display: inline-flex !important;
        min-width: 100% !important;
    }

    .tbody .tr {
        background-color: ${colors.white};
        margin-bottom: 2px;
        border-radius: 4px;
        transition: background 0.15s ease, margin-bottom 0.15s ease;

        ${(p) =>
            p.hoverableRow &&
            css`
                &:hover {
                    background-color: #f6f6ff;
                }
            `}

        &.highlighted {
            background-color: ${colors.brandLight};
            position: relative;
            &:before {
                content: url('data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iMTIiIGhlaWdodD0iMTAiIHZpZXdCb3g9IjAgMCAxMiAxMCIgZmlsbD0ibm9uZSIgeG1sbnM9Imh0dHA6Ly93d3cudzMub3JnLzIwMDAvc3ZnIj4KPHBhdGggZmlsbC1ydWxlPSJldmVub2RkIiBjbGlwLXJ1bGU9ImV2ZW5vZGQiIGQ9Ik0xMS42OTUzIDAuMTYyMTA2QzEyLjAyOTQgMC40MzMxNTIgMTIuMDk5NSAwLjk0OTMxNyAxMS44NTE5IDEuMzE0OTlMNi40OTY5MSA5LjIyMTk1QzUuOTE4NDMgMTAuMDc2MSA0LjgxNjQyIDEwLjI1NDQgNC4wMzY4NiA5LjYyTDAuMzAzNzQ0IDYuNTgxODhDLTAuMDI5OTkyOCA2LjMxMDI4IC0wLjA5OTM2MzMgNS43OTQgMC4xNDg4MDEgNS40Mjg3M0MwLjM5Njk2NCA1LjA2MzQ3IDAuODY4Njg4IDQuOTg3NTUgMS4yMDI0MyA1LjI1OTE2TDQuOTM1NTQgOC4yOTcyN0M1LjA0NjkgOC4zODc5MSA1LjIwNDMzIDguMzYyNDMgNS4yODY5NyA4LjI0MDQxTDEwLjY0MTkgMC4zMzM0NDhDMTAuODg5NiAtMC4wMzIyMjc4IDExLjM2MTIgLTAuMTA4OTQgMTEuNjk1MyAwLjE2MjEwNloiIGZpbGw9IiM2NDY2RTkiLz4KPC9zdmc+Cg==');
                position: absolute;
                left: 5px;
                top: 50%;
                transform: translateY(-50%);
            }
        }

        &.selectable {
            &.selected {
                background-color: ${colors.brandLight};
            }
        }

        &.expanded {
            margin-bottom: 0;
        }
    }

    .tbody .tr .td {
        position: relative;
        &:after {
            position: absolute;
            content: '';
            width: 1px;
            height: calc(100% - 20px);
            margin-top: 5px;
            margin-bottom: 5px;
            background-color: ${colors.border};
            top: 5px;
            right: 0;
        }
    }
`;

export const Header = styled.header`
    box-sizing: border-box;
    display: flex;
    width: 100%;
    background: ${colors.white};
    flex-direction: column;
    justify-content: flex-start;
    align-items: center;
`;

export const HeaderContainer = styled.div<{alignRight?: boolean}>`
    box-sizing: border-box;
    width: 100%;
    display: flex;
    padding: 20px;
    justify-content: ${(p) => (p.alignRight ? 'flex-end' : 'space-between')};
    align-items: center;
    gap: 20px;
`;

export const HeaderControls = styled.div`
    flex: 1;
`;

export const HeaderActionsWrapper = styled.div`
    display: flex;
    flex-flow: row nowrap;
    gap: 20px;
`;

export const HeaderAction = styled.div`
    display: flex;
    align-items: center;
    cursor: pointer;

    font-size: 12px;
    font-weight: 700;
    color: ${colors.brandPrimary};

    > span {
        &:not(:last-of-type) {
            margin-right: 10px;
        }
    }
`;

export const TotalRowsCount = styled.span`
    font-size: 12px;
    color: ${colors.textMuted};
    padding-left: 16px;
    line-height: 33px;
`;

export const Footer = styled.footer`
    margin-top: 20px;
    display: flex;
    justify-content: space-between;
    > * {
        max-width: 100%;
    }

    ${Buttons} {
        justify-content: center;
    }
`;

const opacity = keyframes`
  from {
    opacity: 0;
  }

  to {
    opacity: 1;
  }
`;

export const SpinnerLayer = styled.div`
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    z-index: 1;
    background-color: rgba(255, 255, 255, 0.7);
    display: flex;
    justify-content: center;
    align-items: center;
    animation: ${opacity} 2s ease;

    > * {
        position: sticky;
        top: 20%;
        bottom: 20%;
    }
`;

export const NoValuesContainer = styled.div``;

export const NoValuesLabel = styled.div<{verticalTranslate?: string}>`
    position: absolute;
    top: 50%;
    left: 50%;
    transform: translate(-50%, ${(p) => p.verticalTranslate ?? '-50%'});
    padding: 10px;
    color: ${colors.textMuted};
`;

export const HeaderCell = styled.div`
    position: relative;
`;

export const CellContainer = styled.div<{cellContainerSettings?: string}>`
    display: inline-flex;
    align-items: center;
    min-height: 50px;
    ${(p) => p.cellContainerSettings ?? ''}
`;

export const ExpandedRow = styled.div<{isExpanded?: boolean}>`
    display: grid;
    grid-template-columns: 1fr;
    grid-template-rows: ${(p) => (p.isExpanded ? '1fr' : '0fr')};
    transition: grid 0.2s ease-in-out, margin-bottom 0.15s ease-in-out;
    background-color: ${colors.white};
    margin-bottom: ${(p) => (p.isExpanded ? '2px' : '0')};
`;

export const ExpandedCellWrapper = styled.div`
    grid-column: 1 / -1;
    overflow: hidden;
`;

export const ExpandedCell = styled.div`
    padding: 8px;
`;

export const ExpandToggle = styled.div<{isExpanded?: boolean}>`
    display: grid;
    place-content: center;
    cursor: pointer;
    user-select: none;

    & > * {
        transition: transform 0.2s ease-in-out;
    }
`;
